import React from 'react';
import { SortDirection } from '../../hooks/sorting/types';
import './FilterableTableHeader.scss';

interface FilterableTableHeaderProps {
  columnId: string;
  resizeColumnId: string;
  header: string;
  sortable?: boolean;
  isSorted: boolean;
  sortDirection: SortDirection;
  onSort?: (columnId: string) => void;
  className?: string;
  /** Plain-language explanation of the column, shown on hover/focus. */
  description?: string;
  onShowTooltip?: (content: React.ReactNode, anchor: HTMLElement | null) => void;
  onHideTooltip?: () => void;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  onResize?: (columnId: string, width: number) => void;
}

export function FilterableTableHeader({
  columnId,
  resizeColumnId,
  header,
  sortable = false,
  isSorted,
  sortDirection,
  onSort,
  className = '',
  description,
  onShowTooltip,
  onHideTooltip,
  width,
  minWidth = 80,
  maxWidth = 600,
  resizable = true,
  onResize,
}: FilterableTableHeaderProps) {
  const showDescription = (event: React.SyntheticEvent<HTMLElement>) => {
    if (description) onShowTooltip?.(description, event.currentTarget);
  };

  const handleResizeStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!resizable || !onResize) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const headerCell = event.currentTarget.closest('th');

    if (!headerCell) {
      return;
    }

    const startX = event.clientX;
    const startWidth = width ?? headerCell.getBoundingClientRect().width;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - startX;

      const nextWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + delta));

      onResize(resizeColumnId, nextWidth);
    };

    const handlePointerUp = () => {
      document.body.classList.remove('table-column-resizing');

      window.removeEventListener('pointermove', handlePointerMove);

      window.removeEventListener('pointerup', handlePointerUp);
    };

    document.body.classList.add('table-column-resizing');

    window.addEventListener('pointermove', handlePointerMove);

    window.addEventListener('pointerup', handlePointerUp);
  };

  return (
    <th
      className={[
        className,
        'filterable-header',
        sortable ? 'sortable-header' : '',
        resizable ? 'filterable-header--resizable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      onMouseEnter={showDescription}
      onMouseLeave={onHideTooltip}
    >
      <div className="filterable-header__inner">
        <button
          className="sortable-header__button filterable-header__sort"
          onClick={() => sortable && onSort?.(columnId)}
          type="button"
          disabled={!sortable}
          aria-label={
            [sortable ? `Sort by ${header}` : header, description].filter(Boolean).join('. ') ||
            header
          }
          onFocus={showDescription}
          onBlur={onHideTooltip}
        >
          <span className="sortable-header__text">{header}</span>

          {sortable && (
            <span className="sortable-header__indicator" aria-hidden="true">
              {isSorted ? (
                sortDirection === 'asc' ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 3L2 7h8L6 3z" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 9L2 5h8L6 9z" />
                  </svg>
                )
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" opacity="0.3">
                  <path d="M6 3L4 5h4L6 3zM6 9L4 7h4L6 9z" />
                </svg>
              )}
            </span>
          )}
        </button>
      </div>

      {resizable && onResize && (
        <button
          type="button"
          className="filterable-header__resize-handle"
          aria-label={`Resize ${header} column`}
          title={`Resize ${header} column`}
          onPointerDown={handleResizeStart}
          onClick={(event) => {
            // Prevent the resize handle from triggering sorting.
            event.preventDefault();
            event.stopPropagation();
          }}
        />
      )}
    </th>
  );
}
